import { toStatusColor } from '@interacta-shared/util';
import {
    toAISharedDeltaReferences,
    toAISharedGenericReferences,
    toAISharedMessageAttachment,
    toAISharedModelMessage,
} from '@modules/ai-shared/models/ai-shared.deserialize';
import {
    AISharedMessageType,
    AISharedRole,
} from '@modules/ai-shared/models/ai-shared.model';
import { Server2Delta } from '@modules/core/helpers/delta/server-2-delta.class';
import { AIConvoAgent } from './ai-convo-agent.model';
import { AIConvoHistoryItem } from './ai-convo-history.model';
import { AIConvoGenericMessage } from './ai-convo-message.model';
import {
    AIConvoAskChannel,
    AIConvoThinkingTransport,
} from './ai-convo-thinking-transport.model';
import {
    AIConvoThinkingEvent,
    AIConvoThinkingParams,
    AIConvoThinkingTool,
} from './ai-convo-thinking.model';

export function toAIConvoAgent(record: any): AIConvoAgent {
    return {
        id: record.id,
        name: record.name,
        avatar: record.avatar ?? undefined,
        avatarFull: record.avatarFull ?? undefined,
        description: record.description ?? undefined,
        welcomeMessage: record.welcomeMessage ?? undefined,
        color: toStatusColor(record.colorCode),
    };
}

export function toAIConvoHistoryItem(record: any): AIConvoHistoryItem {
    return {
        id: record.id,
        sessionId: record.sessionId,
        title: record.title ?? null,
        creationTimestamp: new Date(record.creationTimestamp),
        lastModifyTimestamp: new Date(record.lastUpdateTimestamp),
    };
}

export function toAIConvoHistoryMessage(record: any): AIConvoGenericMessage {
    const timestamp = new Date(record.creationTimestamp);

    if (record.role === AISharedRole.USER) {
        return {
            id: record.id,
            role: AISharedRole.USER,
            content: record.message,
            timestamp,
            attachment: record.attachment
                ? toAISharedMessageAttachment(record.attachment)
                : undefined,
            reference: toAISharedGenericReferences(
                record.questionSources ?? [],
            )[0],
            smartActionId: record.smartActionId ?? null,
        };
    }

    return {
        id: record.id,
        role: AISharedRole.MODEL,
        content: record.message,
        timestamp,
        alreadyAnimated: true,
        messageDelta: record.messageDelta
            ? new Server2Delta().process(record.messageDelta)
            : null,
        references: toAISharedGenericReferences(record.deltaSources ?? []),
        deltaReferences: toAISharedDeltaReferences(
            record.deltaReferences ?? [],
            record.deltaSources ?? [],
        ),
        messageType: AISharedMessageType.STANDARD,
    };
}

export function toAIConvoThinkingTransport(
    record: any,
): AIConvoThinkingTransport {
    return {
        connection: {
            apiKey: record.connection.apiKey,
            authDomain: record.connection.authDomain,
            projectId: record.connection.projectId,
            databaseId: record.connection.databaseId,
            messagingSenderId: record.connection.messagingSenderId,
        },
        auth: {
            token: record.auth.token,
        },
    };
}

export function toAIConvoAskChannel(record: any): AIConvoAskChannel {
    return {
        documentId: record.documentId,
        collectionId: record.collectionId,
        eventsCollectionId: record.eventsCollectionId,
    };
}

const THINKING_TOOLS: Record<string, AIConvoThinkingTool> = {
    semantic_search: AIConvoThinkingTool.SEMANTIC_SEARCH,
    list_communities: AIConvoThinkingTool.LIST_COMMUNITIES,
    read_community: AIConvoThinkingTool.COMMUNITY_INFO,
    search_posts: AIConvoThinkingTool.SEARCH_POSTS,
    read_post: AIConvoThinkingTool.READ_POST,
    analyze_attachment: AIConvoThinkingTool.ANALYZE_ATTACHMENT,
};

function toAIConvoThinkingParams(
    tool: AIConvoThinkingTool,
    parameters: any,
): AIConvoThinkingParams {
    const { text, communityName, postTitle, attachmentName } = parameters ?? {};
    return {
        ...(tool === AIConvoThinkingTool.SEMANTIC_SEARCH
            ? { query: text }
            : { text }),
        communityName,
        postTitle,
        fileName: attachmentName,
    };
}

/** Null for an event type the FE does not know: the channel can carry kinds added by the BE later. */
export function toAIConvoThinkingEvent(
    record: any,
): AIConvoThinkingEvent | null {
    switch (record.type) {
        case 'title':
            return { type: 'conversation_title', title: record.title };
        case 'answer':
            return {
                type: 'result',
                response: {
                    sessionId: null,
                    answer: toAISharedModelMessage(record.answer),
                },
                historyId: record.sessionId,
            };
        case 'error':
            return { type: 'error', code: record.code };
        case 'thinking': {
            const tool =
                THINKING_TOOLS[record.tool] ?? AIConvoThinkingTool.UNKNOWN;
            return {
                type: 'thinking',
                step: {
                    id: record.seq,
                    tool,
                    params: toAIConvoThinkingParams(tool, record.parameters),
                },
            };
        }
        default:
            return null;
    }
}
